import { ref, push, set } from 'firebase/database';
import { motion } from 'framer-motion';
import { X, Upload, AlertCircle } from 'lucide-react';
import { MUSIC_TYPES, validateMusicForm } from '../utils/uploadValidation';

export function MusicUploadForm({
  isOpen,
  onClose,
  musicForm,
  setMusicForm,
  fileUploaded,
  uploadedFilePath,
  uploadProgress,
  uploadError,
  fileInputRef,
  isUploading,
  canUpload,
  db,
  currentUser,
  onUploadSuccess,
  onFileUpload,
  showToast,
  resetUpload
}) {
  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateMusicForm(musicForm, fileUploaded, showToast)) return;
    if (!canUpload()) {
      showToast('You have reached your music upload limit', 'error');
      return;
    }

    try {
      const musicRef = push(ref(db, `artistContent/music`));
      const id = musicRef.key;

      await set(musicRef, {
        id,
        artistId: currentUser.uid,
        artistName: currentUser.displayName || 'Unknown Artist',
        name: musicForm.name,
        artist: musicForm.artist,
        album: musicForm.album,
        type: musicForm.type,
        trackNumber: parseInt(musicForm.trackNumber),
        url: musicForm.url,
        description: musicForm.description || '',
        status: 'pending',
        uploadedAt: new Date().toISOString(),
        approvedAt: '',
        approvedById: '',
        approvedByName: '',
        rejectedById: '',
        rejectedByName: '',
        storagePath: uploadedFilePath
      });

      showToast('🎵 Music uploaded! Awaiting admin approval.', 'success');
      onUploadSuccess();
    } catch (err) {
      console.error('Error uploading music:', err);
      showToast('Failed to upload music', 'error');
    }
  };

  const handleCancel = async () => {
    await resetUpload();
    setMusicForm({ name: '', artist: '', album: '', type: 'song', trackNumber: '', description: '' });
    onClose();
  };

  const handleRetryUpload = async () => {
    await resetUpload();
    setMusicForm({ ...musicForm, url: '' });
    fileInputRef.current?.click();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={handleCancel}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-xl shadow-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">Upload Music</h3>
          <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Song Name *</label>
              <input
                type="text"
                value={musicForm.name}
                onChange={(e) => setMusicForm({ ...musicForm, name: e.target.value })}
                placeholder="e.g., Ikaw Ang Kusog"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2">Artist Name *</label>
              <input
                type="text"
                value={musicForm.artist}
                onChange={(e) => setMusicForm({ ...musicForm, artist: e.target.value })}
                placeholder="e.g., The Asidors"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Music Type *</label>
              <select
                value={musicForm.type}
                onChange={(e) => setMusicForm({ ...musicForm, type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white"
              >
                {MUSIC_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2">Album Name *</label>
              <input
                type="text"
                value={musicForm.album}
                onChange={(e) => setMusicForm({ ...musicForm, album: e.target.value })}
                placeholder="e.g., Greatest Hits"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2">Track Number *</label>
              <input
                type="number"
                min="1"
                value={musicForm.trackNumber}
                onChange={(e) => setMusicForm({ ...musicForm, trackNumber: e.target.value })}
                placeholder="e.g., 1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">Description (Optional)</label>
            <textarea
              value={musicForm.description}
              onChange={(e) => setMusicForm({ ...musicForm, description: e.target.value })}
              placeholder="Add a description"
              rows="3"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">Upload Music File *</label>
            <input
              type="file"
              accept=".mp3,.wav,audio/mpeg,audio/wav"
              ref={fileInputRef}
              onChange={(e) => onFileUpload(e.target.files[0])}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition"
            >
              <Upload size={16} /> Upload Music
            </button>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mt-3">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">Upload Progress</span>
                  <span className="text-sm font-semibold text-blue-600">{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <motion.div
                    className="bg-blue-500 h-2.5 rounded-full"
                    animate={{ width: `${uploadProgress}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </div>
            )}

            {fileUploaded && !uploadError && (
              <p className="text-sm text-green-600 mt-2">✓ Music file uploaded</p>
            )}

            {uploadError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex gap-2 items-center justify-between">
                <div className="flex gap-2">
                  <AlertCircle size={18} className="text-red-600 flex-shrink-0" />
                  <p className="text-sm text-red-800">{uploadError}</p>
                </div>
                <button
                  type="button"
                  onClick={handleRetryUpload}
                  className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs font-medium"
                >
                  Retry
                </button>
              </div>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-xs text-blue-700">
            Your music will be reviewed by our admin team before appearing publicly.
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={isUploading || !fileUploaded}
              className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold disabled:opacity-50"
            >
              Upload Music
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg font-semibold"
            >
              Cancel
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
}